import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { siteData } from '../../../config/siteData';

const About = () => {
  const [animationKey, setAnimationKey] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimationKey(prev => prev + 1);
          }
        });
      },
      { threshold: 0.3 }
    );

    const section = document.getElementById('about');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  return (
    <section id="about" className="pt-8 sm:pt-12 lg:pt-16 pb-14 sm:pb-20 lg:pb-24 bg-gray-100">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
        <motion.div
          key={`about-header-${animationKey}`}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-10 sm:mb-12 lg:mb-14"
        >
          <div className="text-navy text-xs sm:text-sm font-serif tracking-wider mb-2 sm:mb-3">WHO WE ARE</div>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold mb-3 sm:mb-4 px-4">
            <span className="text-navy">Building </span>
            <span className="text-gold italic">Generational </span>
            <span className="text-navy">Prosperity</span>
          </h2>
          <p className="text-text-light text-sm sm:text-base max-w-2xl mx-auto leading-relaxed px-4">
            Dulair Private Family Office represents the pinnacle of discretionary wealth management, combining traditional values with innovative investment strategies to preserve and grow family legacies.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
          {/* Left Card */}
          <motion.div
            key={`about-left-${animationKey}`}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ y: -8, boxShadow: "0 0 0 2px rgba(212,175,55,0.6), 0 24px 48px rgba(0,0,0,0.3)" }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative bg-gradient-to-br from-navy to-gray rounded-2xl p-6 sm:p-8 lg:p-10 text-white overflow-hidden group cursor-pointer"
          >
            <div className="absolute top-5 right-5 sm:top-6 sm:right-6 w-9 h-9 sm:w-10 sm:h-10 bg-gold rounded-full flex items-center justify-center cursor-pointer group-hover:rotate-45 group-hover:scale-125 group-hover:shadow-[0_0_18px_rgba(212,175,55,0.85)] transition-all duration-300 flex-shrink-0">
              <i className="ri-arrow-right-up-line text-navy text-base sm:text-lg"></i>
            </div>
            
            <div className="relative z-10">
              <div className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mb-2 sm:mb-3 break-words">{siteData.assetsUnderManagement}</div>
              <div className="text-base sm:text-lg font-medium mb-1.5">Assets Under Management</div>
              <div className="text-white/80 mb-4 sm:mb-6 text-sm">Across Three Core Sectors</div>
              <div className="text-white/70 italic text-xs">Trusted by generations of families</div>
            </div>
          </motion.div>

          {/* Right Card */}
          <motion.div
            key={`about-right-${animationKey}`}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ y: -8, boxShadow: "0 0 0 2px rgba(212,175,55,0.6), 0 24px 48px rgba(0,0,0,0.3)" }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative bg-gradient-to-br from-gray to-navy rounded-2xl p-6 sm:p-8 lg:p-10 text-white overflow-hidden group cursor-pointer"
          >
            <div className="absolute top-5 right-5 sm:top-6 sm:right-6 w-9 h-9 sm:w-10 sm:h-10 bg-gold rounded-full flex items-center justify-center cursor-pointer group-hover:rotate-45 group-hover:scale-125 group-hover:shadow-[0_0_18px_rgba(212,175,55,0.85)] transition-all duration-300 flex-shrink-0">
              <i className="ri-arrow-right-up-line text-navy text-base sm:text-lg"></i>
            </div>
            
            <div className="relative z-10">
              <div className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mb-2 sm:mb-3 break-words">{siteData.yearsExperience}</div>
              <div className="text-base sm:text-lg font-medium mb-1.5">Years of Combined Experience</div>
              <div className="text-white/80 mb-2 sm:mb-3 text-sm">in Property, Healthcare, and Strategic Investments</div>
              <div className="text-white/70 text-xs leading-relaxed">
                Our seasoned team brings decades of expertise in identifying and nurturing high-value opportunities that align with our clients&apos; long-term objectives and values.
              </div>
            </div>
          </motion.div>
        </div>

        {/* Additional Content */}
        <motion.div
          key={`about-bottom-${animationKey}`}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-8 sm:mt-10 lg:mt-12 bg-white rounded-2xl p-6 sm:p-8 lg:p-10 shadow-xl"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="group cursor-pointer">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gold/10 rounded-xl flex items-center justify-center mb-3 sm:mb-4 flex-shrink-0 group-hover:bg-gold/20 group-hover:scale-110 transition-all duration-300">
                <i className="ri-shield-check-line text-gold text-lg sm:text-xl group-hover:scale-110 transition-transform duration-300"></i>
              </div>
              <h3 className="text-navy text-base sm:text-lg font-bold mb-2 group-hover:text-gold transition-colors duration-300">Discretion &amp; Trust</h3>
              <p className="text-text-light text-sm leading-relaxed">
                We uphold the highest standards of confidentiality and fiduciary responsibility, ensuring your family&apos;s wealth is managed with absolute discretion and integrity.
              </p>
            </div>
            <div className="group cursor-pointer">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gold/10 rounded-xl flex items-center justify-center mb-3 sm:mb-4 flex-shrink-0 group-hover:bg-gold/20 group-hover:scale-110 transition-all duration-300">
                <i className="ri-line-chart-line text-gold text-lg sm:text-xl group-hover:scale-110 transition-transform duration-300"></i>
              </div>
              <h3 className="text-navy text-base sm:text-lg font-bold mb-2 group-hover:text-gold transition-colors duration-300">Strategic Excellence</h3>
              <p className="text-text-light text-sm leading-relaxed">
                Our investment philosophy combines rigorous analysis with forward-thinking strategies, delivering consistent returns while managing risk across diverse asset classes.
              </p>
            </div>
            <div className="group cursor-pointer">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gold/10 rounded-xl flex items-center justify-center mb-3 sm:mb-4 flex-shrink-0 group-hover:bg-gold/20 group-hover:scale-110 transition-all duration-300">
                <i className="ri-team-line text-gold text-lg sm:text-xl group-hover:scale-110 transition-transform duration-300"></i>
              </div>
              <h3 className="text-navy text-base sm:text-lg font-bold mb-2 group-hover:text-gold transition-colors duration-300">Bespoke Service</h3>
              <p className="text-text-light text-sm leading-relaxed">
                Every family is unique. We craft personalized investment solutions that reflect your values, goals, and vision for future generations.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
